import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {AppState} from 'app/store';
import {fetchSeatMap, clearSeatMap} from '../store/seat-map.slice';
import PageHeader from 'app/main/shared-components/page-header/PageHeader';
import {
  CircularProgress, Tooltip, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Box,
  Typography, Divider, Switch, FormControlLabel, Grid,
} from '@material-ui/core';
import {seatMapStyles} from './seat-map.styles';
import {aircraftDetailStyles} from 'app/main/configurations/aircrafts/aircrafts.styles';
import {SeatInfoResponse} from 'app/types/service-types/AircraftTypes';
import history from '@history';
import clsx from 'clsx';
import {FlightService} from 'app/services';
import {successMsg, errorMsg} from 'app/utils/MessageUtils';

const FlightSeatMap = () => {
  const classes = seatMapStyles();
  const aircraftClasses = aircraftDetailStyles();
  const dispatch = useDispatch();
  const {flightId} = useParams<{flightId: string}>();

  /* ----- Pricing override dialogs ----- */
  const [classPriceOpen, setClassPriceOpen] = useState(false);
  const [classPriceForm, setClassPriceForm] = useState({seatClassName: '', priceOverride: ''});
  const [classPriceLoading, setClassPriceLoading] = useState(false);

  const [seatPriceOpen, setSeatPriceOpen] = useState(false);
  const [seatPriceForm, setSeatPriceForm] = useState({seatLabel: '', priceOverride: ''});
  const [seatPriceLoading, setSeatPriceLoading] = useState(false);

  /* ----- Seat detail dialog ----- */
  const [seatDetailOpen, setSeatDetailOpen] = useState(false);
  const [seatDetailData, setSeatDetailData] = useState<SeatInfoResponse | null>(null);
  const [seatDetailForm, setSeatDetailForm] = useState({status: '', priceOverride: ''});
  const [seatDetailLoading, setSeatDetailLoading] = useState(false);

  const {seatMap, loading} = useSelector(
    (state: AppState) => state.flights.seatMap
  );

  useEffect(() => {
    if (flightId) {
      dispatch(fetchSeatMap(flightId));
    }
    return () => {
      dispatch(clearSeatMap());
    };
  }, [flightId]);

  /* Unique seat class names for dropdown */
  const seatClassNames = useMemo(() => {
    if (!seatMap) return [];
    const set = new Set<string>();
    seatMap.rows.forEach(r => r.seats.forEach(s => { if (s.seatClassName) set.add(s.seatClassName); }));
    return Array.from(set);
  }, [seatMap]);

  /* Calculate special rows and aisle position */
  const {aisleAfterIndex, exitRows, overWingRows, classBoundaries} = useMemo(() => {
    if (!seatMap) return {aisleAfterIndex: -1, exitRows: new Set<number>(), overWingRows: new Set<number>(), classBoundaries: new Set<number>()};
    
    const exits = new Set<number>();
    const wings = new Set<number>();
    const boundaries = new Set<number>();
    let aisleIdx = -1;

    // Determine aisle position (largest gap in column sequence)
    const cols = seatMap.columns;
    if (cols.length > 2) {
      const midPoint = Math.floor(cols.length / 2);
      aisleIdx = midPoint - 1; // Aisle after this index
    }

    // Find exit rows, over-wing rows
    seatMap.rows.forEach(row => {
      row.seats.forEach(seat => {
        if (seat.nearExit) exits.add(row.rowNumber);
        if (seat.overWing) wings.add(row.rowNumber);
      });
    });

    // Find class boundaries - compare row by row
    // Create a map of row number to seat class
    const rowClassMap = new Map<number, string>();
    seatMap.rows.forEach(row => {
      const seatClass = row.seats.find(s => s.seatClassName)?.seatClassName || '';
      if (seatClass) {
        rowClassMap.set(row.rowNumber, seatClass);
      }
    });

    // Sort row numbers and compare consecutive rows
    const sortedRowNumbers = Array.from(rowClassMap.keys()).sort((a, b) => a - b);
    for (let i = 1; i < sortedRowNumbers.length; i++) {
      const prevRowNum = sortedRowNumbers[i - 1];
      const currRowNum = sortedRowNumbers[i];
      const prevClass = rowClassMap.get(prevRowNum);
      const currClass = rowClassMap.get(currRowNum);
      
      // If class changed, add boundary at current row
      if (prevClass && currClass && prevClass !== currClass) {
        boundaries.add(currRowNum);
      }
    }

    return {aisleAfterIndex: aisleIdx, exitRows: exits, overWingRows: wings, classBoundaries: boundaries};
  }, [seatMap]);

  /* Get seat helper - find seat by row and column */
  const getSeatByPosition = (rowNum: number, col: string) => {
    if (!seatMap) return null;
    const row = seatMap.rows.find(r => r.rowNumber === rowNum);
    return row?.seats.find(s => s.columnLetter === col) || null;
  };

  const handleSeatClick = (seat: SeatInfoResponse) => {
    // Open seat detail dialog for any seat
    setSeatDetailData(seat);
    setSeatDetailForm({
      status: seat.status,
      priceOverride: seat.price != null ? String(seat.price) : '',
    });
    setSeatDetailOpen(true);
  };

  /* ----- Update individual seat ----- */
  const handleSeatDetailSave = async () => {
    if (!seatDetailData) return;
    setSeatDetailLoading(true);
    try {
      const body: { status?: string; priceOverride?: number | null } = {};
      if (seatDetailForm.status && seatDetailForm.status !== seatDetailData.status) {
        body.status = seatDetailForm.status;
      }
      const newPrice = seatDetailForm.priceOverride !== '' ? Number(seatDetailForm.priceOverride) : null;
      if (newPrice !== seatDetailData.price) {
        body.priceOverride = newPrice;
      }
      if (Object.keys(body).length > 0) {
        await FlightService.updateFlightSeat(flightId, seatDetailData.id, body);
        dispatch(successMsg(`Seat ${seatDetailData.label} updated`));
        dispatch(fetchSeatMap(flightId)); // Refresh
      }
      setSeatDetailOpen(false);
      setSeatDetailData(null);
    } catch (err: any) {
      dispatch(errorMsg(err?.response?.data?.message || 'Failed to update seat'));
    }
    setSeatDetailLoading(false);
  };

  /* ----- Override by class ----- */
  const handleOverrideByClass = async () => {
    if (!classPriceForm.seatClassName || !classPriceForm.priceOverride) return;
    setClassPriceLoading(true);
    try {
      await FlightService.overridePriceByClass(flightId, classPriceForm.seatClassName, Number(classPriceForm.priceOverride));
      dispatch(successMsg(`Price updated for ${classPriceForm.seatClassName}`));
      setClassPriceOpen(false);
      setClassPriceForm({seatClassName: '', priceOverride: ''});
      dispatch(fetchSeatMap(flightId)); // Refresh
    } catch (err: any) {
      dispatch(errorMsg(err?.response?.data?.message || 'Failed to override class price'));
    }
    setClassPriceLoading(false);
  };

  /* ----- Override by seat ----- */
  const handleOverrideBySeat = async () => {
    if (!seatPriceForm.seatLabel || !seatPriceForm.priceOverride) return;
    setSeatPriceLoading(true);
    try {
      await FlightService.overridePriceBySeat(flightId, seatPriceForm.seatLabel, Number(seatPriceForm.priceOverride));
      dispatch(successMsg(`Price updated for seat ${seatPriceForm.seatLabel}`));
      setSeatPriceOpen(false);
      setSeatPriceForm({seatLabel: '', priceOverride: ''});
      dispatch(fetchSeatMap(flightId)); // Refresh
    } catch (err: any) {
      dispatch(errorMsg(err?.response?.data?.message || 'Failed to override seat price'));
    }
    setSeatPriceLoading(false);
  };

  const getSeatClassName = (seat: SeatInfoResponse): string => {
    // Always include base aircraft seat style
    const baseClass = aircraftClasses.seat;
    
    // Add status-specific styling
    switch (seat.status) {
      case 'AVAILABLE':
        if (seat.blocked) {
          return clsx(baseClass, aircraftClasses.seatBlocked);
        }
        return clsx(baseClass, classes.seatAvailable);
      case 'BOOKED':
      case 'RESERVED':
        return clsx(baseClass, classes.seatBooked);
      case 'BLOCKED':
      case 'UNAVAILABLE':
      case 'LOCKED':
        return clsx(baseClass, aircraftClasses.seatBlocked);
      default:
        return baseClass;
    }
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 40}}>
        <CircularProgress/>
      </div>
    );
  }

  if (!seatMap) {
    return (
      <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
        Seat map not available for this flight
      </div>
    );
  }

  const gridCols = `40px repeat(${seatMap.columns.length}, 40px)`;

  return (
    <div className={classes.root}>
      <PageHeader
        title={`Seat Map - ${seatMap.flightNumber}`}
        desc={`${seatMap.aircraftName} (${seatMap.aircraftCode})`}
        backButton={() => history.goBack()}
      />

      {/* Flight Info */}
      <div className={classes.mapCard}>
        <div className={classes.flightInfo}>
          <div className={classes.flightInfoItem}>
            <label>Flight</label>
            <span>{seatMap.flightNumber}</span>
          </div>
          <div className={classes.flightInfoItem}>
            <label>Aircraft</label>
            <span>{seatMap.aircraftName}</span>
          </div>
          <div className={classes.flightInfoItem}>
            <label>Configuration</label>
            <span>{seatMap.aircraftCode}</span>
          </div>
          <div className={classes.flightInfoItem}>
            <label>Rows</label>
            <span>{seatMap.totalRows}</span>
          </div>
          <div className={classes.flightInfoItem}>
            <label>Currency</label>
            <span>{seatMap.currency}</span>
          </div>
        </div>
      </div>

      {/* Pricing Override Actions */}
      <div className={classes.mapCard}>
        <div style={{fontWeight: 600, marginBottom: 10, color: '#0D3B66', fontSize: 14}}>
          Seat Pricing Override
        </div>
        <Box display="flex" gridGap={12} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setClassPriceOpen(true)}
          >
            Override Price by Class
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setSeatPriceOpen(true)}
          >
            Override Price by Seat
          </Button>
        </Box>
      </div>

      {/* Legend */}
      <div className={classes.mapCard}>
        <div className={classes.legendContainer}>
          <div className={classes.legendItem}>
            <div className={classes.legendSwatch} style={{backgroundColor: '#e8f5e9', borderColor: '#a5d6a7'}}/>
            Available
          </div>
          <div className={classes.legendItem}>
            <div className={classes.legendSwatch} style={{backgroundColor: '#ffebee', borderColor: '#ef9a9a'}}/>
            Booked / Reserved
          </div>
          <div className={classes.legendItem}>
            <div className={classes.legendSwatch} style={{backgroundColor: '#eeeeee', borderColor: '#e0e0e0'}}/>
            Blocked / Unavailable
          </div>
        </div>
        <Typography variant="caption" color="textSecondary">
          Click any seat to view details and manage its status or price.
        </Typography>
      </div>

      {/* ============ SEAT MAP — Airplane Shape ============ */}
      <div className={classes.mapCard}>
        <div style={{fontWeight: 600, marginBottom: 12, color: '#0D3B66', fontSize: 14}}>Seat Map</div>

        <div className={aircraftClasses.fuselageWrapper}>
          <div className={aircraftClasses.fuselage}>
            {/* Cockpit Nose */}
            <div className={aircraftClasses.noseOverlay}>
              <span className={aircraftClasses.cockpitIcon}>✈</span>
            </div>

            {/* Column Header Row */}
            <div className={aircraftClasses.colHeaderRow}>
              <div className={aircraftClasses.rowLabel}/>
              {seatMap.columns.map((col: string, ci: number) => (
                <React.Fragment key={col}>
                  {ci === aisleAfterIndex + 1 && ci > 0 && <div className={aircraftClasses.aisleGap}/>}
                  <div style={{width: 34, margin: '0 2px'}} className={aircraftClasses.colHeaderLabel}>{col}</div>
                </React.Fragment>
              ))}
              <div className={aircraftClasses.rowLabel}/>
            </div>

            {/* Seat Rows */}
            {Array.from({length: seatMap.totalRows}, (_, i) => i + 1).map(rowNum => {
              const isExitRow = exitRows.has(rowNum);
              const isOverWing = overWingRows.has(rowNum);
              const showDivider = classBoundaries.has(rowNum);
              return (
                <React.Fragment key={rowNum}>
                  {showDivider && <div className={aircraftClasses.classDivider}/>}
                  <div className={aircraftClasses.seatRow} style={isOverWing ? {background: 'rgba(144,164,174,0.07)', borderRadius: 4} : undefined}>
                    {/* Left row number */}
                    <div className={aircraftClasses.rowLabel}>{rowNum}</div>

                    {/* Seats with aisle gap */}
                    {seatMap.columns.map((col: string, ci: number) => {
                      const seat = getSeatByPosition(rowNum, col);
                      return (
                        <React.Fragment key={col}>
                          {ci === aisleAfterIndex + 1 && ci > 0 && <div className={aircraftClasses.aisleGap}/>}
                          {seat ? (
                            <Tooltip
                              title={
                                <div style={{fontSize: 12}}>
                                  <div style={{fontWeight: 600}}>{seat.label}</div>
                                  <div>{seat.seatClassName}</div>
                                  <div>{seat.price} {seatMap.currency}</div>
                                  <div style={{textTransform: 'capitalize'}}>{seat.status.toLowerCase()}</div>
                                  {seat.nearWindow && <div>Window</div>}
                                  {seat.nearAisle && <div>Aisle</div>}
                                  {seat.nearExit && <div>Exit</div>}
                                  {seat.overWing && <div>Over Wing</div>}
                                </div>
                              }
                              arrow
                            >
                              <div
                                className={getSeatClassName(seat)}
                                onClick={() => handleSeatClick(seat)}
                                style={{cursor: 'pointer'}}
                              >
                                {seat.status === 'BLOCKED' || seat.status === 'UNAVAILABLE' || seat.blocked ? '✕' : seat.label}
                              </div>
                            </Tooltip>
                          ) : (
                            <div style={{width: 34, height: 34, margin: '0 2px'}}/>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Right row number */}
                    <div className={aircraftClasses.rowLabel}>{rowNum}</div>

                    {/* Exit row indicators */}
                    {isExitRow && (
                      <>
                        <span className={`${aircraftClasses.exitLabel} ${aircraftClasses.exitLeft}`}>EXIT</span>
                        <span className={`${aircraftClasses.exitLabel} ${aircraftClasses.exitRight}`}>EXIT</span>
                      </>
                    )}

                    {/* Over-wing indicators */}
                    {isOverWing && overWingRows.has(rowNum) && !overWingRows.has(rowNum - 1) && (
                      <>
                        <span className={`${aircraftClasses.wingIndicator} ${aircraftClasses.wingLeft}`}>▸</span>
                        <span className={`${aircraftClasses.wingIndicator} ${aircraftClasses.wingRight}`}>◂</span>
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {/* Tail */}
            <div className={aircraftClasses.tailSection}>aft</div>
          </div>
        </div>
      </div>

      {/* ===== Override Price by Class Dialog ===== */}
      <Dialog open={classPriceOpen} onClose={() => setClassPriceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Price by Seat Class</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={16}>
            <TextField
              select
              label="Seat Class"
              variant="outlined"
              size="small"
              value={classPriceForm.seatClassName}
              onChange={e => setClassPriceForm(p => ({...p, seatClassName: e.target.value}))}
              fullWidth
            >
              {seatClassNames.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="New Price Override"
              variant="outlined"
              size="small"
              type="number"
              value={classPriceForm.priceOverride}
              onChange={e => setClassPriceForm(p => ({...p, priceOverride: e.target.value}))}
              fullWidth
              InputProps={{inputProps: {min: 0, step: '0.01'}}}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassPriceOpen(false)} disabled={classPriceLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOverrideByClass}
            disabled={classPriceLoading || !classPriceForm.seatClassName || !classPriceForm.priceOverride}
          >
            {classPriceLoading ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Override Price by Seat Label Dialog ===== */}
      <Dialog open={seatPriceOpen} onClose={() => setSeatPriceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Price by Seat Label</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={16}>
            <TextField
              label="Seat Label (e.g. 1A)"
              variant="outlined"
              size="small"
              value={seatPriceForm.seatLabel}
              onChange={e => setSeatPriceForm(p => ({...p, seatLabel: e.target.value.toUpperCase()}))}
              fullWidth
              placeholder="1A"
            />
            <TextField
              label="New Price Override"
              variant="outlined"
              size="small"
              type="number"
              value={seatPriceForm.priceOverride}
              onChange={e => setSeatPriceForm(p => ({...p, priceOverride: e.target.value}))}
              fullWidth
              InputProps={{inputProps: {min: 0, step: '0.01'}}}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeatPriceOpen(false)} disabled={seatPriceLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOverrideBySeat}
            disabled={seatPriceLoading || !seatPriceForm.seatLabel || !seatPriceForm.priceOverride}
          >
            {seatPriceLoading ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Seat Detail / Edit Dialog ===== */}
      <Dialog
        open={seatDetailOpen}
        onClose={() => { setSeatDetailOpen(false); setSeatDetailData(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Seat {seatDetailData?.label} Details</span>
            <Chip
              size="small"
              label={seatDetailData?.status?.toLowerCase()}
              style={{
                textTransform: 'capitalize',
                backgroundColor: seatDetailData?.status === 'AVAILABLE' ? '#e8f5e9'
                  : seatDetailData?.status === 'UNAVAILABLE' ? '#eeeeee'
                  : seatDetailData?.status === 'RESERVED' ? '#fff3e0'
                  : seatDetailData?.status === 'LOCKED' ? '#fce4ec'
                  : '#ffebee',
                color: seatDetailData?.status === 'AVAILABLE' ? '#2e7d32'
                  : seatDetailData?.status === 'UNAVAILABLE' ? '#616161'
                  : seatDetailData?.status === 'RESERVED' ? '#e65100'
                  : seatDetailData?.status === 'LOCKED' ? '#c62828'
                  : '#c62828',
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {seatDetailData && (
            <Box>
              {/* Seat Information */}
              <Typography variant="subtitle2" style={{color: '#0D3B66', marginBottom: 8}}>
                Seat Information
              </Typography>
              <Grid container spacing={2} style={{marginBottom: 16}}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Seat Label</Typography>
                  <Typography variant="body2" style={{fontWeight: 600}}>{seatDetailData.label}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Seat Class</Typography>
                  <Typography variant="body2" style={{fontWeight: 600}}>{seatDetailData.seatClassName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Row</Typography>
                  <Typography variant="body2">{seatDetailData.rowNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Column</Typography>
                  <Typography variant="body2">{seatDetailData.columnLetter}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Current Price</Typography>
                  <Typography variant="body2" style={{fontWeight: 600}}>
                    {seatDetailData.price} {seatMap?.currency}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Blocked (Definition)</Typography>
                  <Typography variant="body2">{seatDetailData.blocked ? 'Yes' : 'No'}</Typography>
                </Grid>
              </Grid>

              {/* Position Features */}
              <Typography variant="subtitle2" style={{color: '#0D3B66', marginBottom: 8}}>
                Position Features
              </Typography>
              <Box display="flex" flexWrap="wrap" gridGap={6} mb={2}>
                {seatDetailData.nearWindow && (
                  <Chip size="small" label="Window" variant="outlined" style={{borderColor: '#42a5f5', color: '#1565c0'}} />
                )}
                {seatDetailData.nearAisle && (
                  <Chip size="small" label="Aisle" variant="outlined" style={{borderColor: '#66bb6a', color: '#2e7d32'}} />
                )}
                {seatDetailData.nearExit && (
                  <Chip size="small" label="Near Exit" variant="outlined" style={{borderColor: '#ef5350', color: '#c62828'}} />
                )}
                {seatDetailData.overWing && (
                  <Chip size="small" label="Over Wing" variant="outlined" style={{borderColor: '#ffa726', color: '#e65100'}} />
                )}
                {seatDetailData.nearLavatory && (
                  <Chip size="small" label="Near Lavatory" variant="outlined" style={{borderColor: '#ab47bc', color: '#7b1fa2'}} />
                )}
                {seatDetailData.allowChildSelected && (
                  <Chip size="small" label="Child Allowed" variant="outlined" style={{borderColor: '#26c6da', color: '#00838f'}} />
                )}
                {!seatDetailData.nearWindow && !seatDetailData.nearAisle && !seatDetailData.nearExit
                  && !seatDetailData.overWing && !seatDetailData.nearLavatory && (
                  <Typography variant="body2" color="textSecondary">No special features</Typography>
                )}
              </Box>

              <Divider style={{margin: '16px 0'}} />

              {/* Editable Fields */}
              <Typography variant="subtitle2" style={{color: '#0D3B66', marginBottom: 12}}>
                Update Seat
              </Typography>
              <Box display="flex" flexDirection="column" gridGap={16}>
                <TextField
                  select
                  label="Status"
                  variant="outlined"
                  size="small"
                  value={seatDetailForm.status}
                  onChange={e => setSeatDetailForm(p => ({...p, status: e.target.value}))}
                  fullWidth
                >
                  <MenuItem value="AVAILABLE">Available</MenuItem>
                  <MenuItem value="UNAVAILABLE">Unavailable (Blocked)</MenuItem>
                  <MenuItem value="LOCKED">Locked</MenuItem>
                  <MenuItem value="RESERVED">Reserved</MenuItem>
                </TextField>
                <TextField
                  label="Price Override"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={seatDetailForm.priceOverride}
                  onChange={e => setSeatDetailForm(p => ({...p, priceOverride: e.target.value}))}
                  fullWidth
                  disabled={seatDetailForm.status === 'RESERVED' || seatDetailForm.status === 'LOCKED'}
                  InputProps={{inputProps: {min: 0, step: '0.01'}}}
                  helperText={`Current: ${seatDetailData.price} ${seatMap?.currency || ''}`}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSeatDetailOpen(false); setSeatDetailData(null); }} disabled={seatDetailLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSeatDetailSave}
            disabled={seatDetailLoading}
          >
            {seatDetailLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlightSeatMap;
